import Pusher from "pusher-js";
import { useEffect, useState, useRef } from "react";
import i18n from "@/i18n";

export interface SubtitlePayload {
  ja?: string;
  en?: string;
  zh?: string;
  originalLanguage?: string;
}

type SubtitleEvent =
  | { subtitle: SubtitlePayload }
  | (SubtitlePayload & Record<string, unknown>)
  | Record<string, unknown>;

type SubtitleItem = { text: string; shownAt: number | null };

// 句読点のみで分割（文字数による分割なし）
function splitSubtitleText(input: string, lang: string): string[] {
  const text = (input || "").replace(/\s+/g, " ").trim();
  if (!text) return [];

  // 80文字以下は分割しない
  if (text.length <= 80) return [text];

  const fullStops = new Set(["。", "！", "？", ".", "!", "?"]);
  const commas = new Set(["、", "，", ","]);

  const segments: string[] = [];
  let buf = "";
  const n = text.length;
  for (let i = 0; i < n; i++) {
    const ch = text[i];
    buf += ch;
    const next = i + 1 < n ? text[i + 1] : "";
    if (fullStops.has(ch) || commas.has(ch)) {
      // 連続する句読点はまとめてバッファに追加し、チェーンの最後で区切る
      if (!(fullStops.has(next) || commas.has(next))) {
        segments.push(buf.trim());
        buf = "";
      }
    }
  }
  if (buf.trim()) segments.push(buf.trim());

  // 連続セグメントを結合して各セグメントが80文字を超えないよう調整
  const MAX = 80;
  const merged: string[] = [];
  let acc = "";
  for (const seg of segments) {
    if (!acc) {
      acc = seg;
      continue;
    }
    if ((acc + seg).length <= MAX) {
      acc += seg;
    } else {
      merged.push(acc);
      acc = seg;
    }
  }
  if (acc) merged.push(acc);

  return merged;
}

export const useSubtitle = (
  pusher: Pusher | null,
  episodeId: string | number | null | undefined
) => {
  const [queue, setQueue] = useState<SubtitleItem[]>([]);

  useEffect(() => {
    if (!pusher || !episodeId) return;

    const channelName = `${episodeId}-viewer-channel`;
    const eventName = `${episodeId}-subtitle-event`;

    let channel = pusher.channel(channelName);
    if (!channel) {
      channel = pusher.subscribe(channelName);
    }

    const handler = async (data: SubtitleEvent) => {
      const raw: any = data;
      let payload: SubtitlePayload | undefined;
      if (raw && typeof raw.subtitle === "object") {
        payload = raw.subtitle as SubtitlePayload;
      } else if (
        raw &&
        (typeof raw.ja === "string" ||
          typeof raw.en === "string" ||
          typeof raw.zh === "string")
      ) {
        payload = {
          ja: typeof raw.ja === "string" ? raw.ja : undefined,
          en: typeof raw.en === "string" ? raw.en : undefined,
          zh: typeof raw.zh === "string" ? raw.zh : undefined,
          originalLanguage:
            typeof raw.originalLanguage === "string"
              ? raw.originalLanguage
              : undefined,
        };
      }

      if (!payload) {
        console.warn("Invalid subtitle payload", data);
        return;
      }

      // オリジナル言語と選択言語が同じ場合は字幕を表示しない
      if (
        payload.originalLanguage &&
        payload.originalLanguage === currentLang
      ) {
        return;
      }

      const text = pickText(payload, currentLang);
      if (!text) {
        console.warn("Subtitle text not found for lang", currentLang, payload);
        return;
      }

      const parts = splitSubtitleText(text, currentLang);
      if (parts.length === 0) return;

      setQueue((prev) => {
        const now = Date.now();
        if (prev.length === 0) {
          return parts.map((t, idx) => ({
            text: t,
            shownAt: idx === 0 ? now : null,
          }));
        }
        return [...prev, ...parts.map((t) => ({ text: t, shownAt: null }))];
      });
    };

    channel.bind(eventName, handler);

    // クリーンアップ関数
    return () => {
      try {
        channel.unbind(eventName, handler);
      } catch {}
      // NOTE: 他インスタンスが存在する可能性があるため、チャンネル自体は解除しない
    };
  }, [pusher, episodeId]);

  // 現在のアプリ言語に応じたテキストへマップ
  const [currentLang, setCurrentLang] = useState<string>(i18n.language);
  useEffect(() => {
    const handler = (lng: string) => setCurrentLang(lng);
    i18n.on("languageChanged", handler);
    return () => {
      i18n.off("languageChanged", handler);
    };
  }, []);
  const pickText = (
    s: SubtitlePayload | undefined,
    lang: string
  ): string | undefined => {
    if (!s || typeof s !== "object") return undefined;
    const byLang = (s as any)[lang];
    if (typeof byLang === "string" && byLang.length > 0) return byLang;
    if (s.originalLanguage) {
      const byOriginal = (s as any)[s.originalLanguage];
      if (typeof byOriginal === "string" && byOriginal.length > 0)
        return byOriginal;
    }
    if (s.ja) return s.ja;
    if (s.en) return s.en;
    if (s.zh) return s.zh;
    return undefined;
  };
  // 表示管理: 最古のみ表示。次の字幕がある場合は4秒、なければ8秒。
  const timerRef = useRef<number | null>(null);
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (queue.length === 0) return;

    const now = Date.now();
    const [first, second] = queue;
    if (first.shownAt === null) {
      setQueue((prev) => {
        if (prev.length === 0) return prev;
        const copy = [...prev];
        copy[0] = { ...copy[0], shownAt: now };
        return copy;
      });
      return;
    }

    const elapsed = now - first.shownAt;
    const total = second ? 4000 : 8000;
    const remaining = Math.max(0, total - elapsed);

    if (remaining <= 0) {
      setQueue((prev) => {
        const rest = prev.slice(1);
        if (rest.length > 0) rest[0] = { ...rest[0], shownAt: Date.now() };
        return rest;
      });
      return;
    }

    timerRef.current = window.setTimeout(() => {
      setQueue((prev) => {
        const rest = prev.slice(1);
        if (rest.length > 0) rest[0] = { ...rest[0], shownAt: Date.now() };
        return rest;
      });
    }, remaining);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [queue]);

  const subtitles = queue.length > 0 ? [queue[0].text] : [];
  return { subtitles };
};
